import { create } from 'zustand';
import { Settings, ThresholdSettings, settingsService, DEFAULT_THRESHOLD_SETTINGS } from '../services/settingsService';
import { produce } from 'immer';
import { useZoneStore } from './zoneStore';

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

interface SettingsState {
  loadedZoneId: string | null;
  settings: Settings | null;
  originalSettings: Settings | null;
  /** Always non-null. Initialized with defaults, updated when settings load. */
  thresholds: ThresholdSettings;
  isLoading: boolean;
  hasUnsavedChanges: boolean;
  error: string | null;

  // Actions
  loadSettings: () => Promise<void>;
  updateSettings: (settings: Settings) => void;
  saveSettings: () => Promise<void>;
  discardChanges: () => void;
}

let settingsRequest = 0;
export const useSettingsStore = create<SettingsState>((set, get) => ({
  loadedZoneId: null,
  settings: null,
  originalSettings: null,
  thresholds: DEFAULT_THRESHOLD_SETTINGS,
  isLoading: false,
  hasUnsavedChanges: false,
  error: null,

  loadSettings: async () => {
    const request = ++settingsRequest;
    const zoneId = useZoneStore.getState().activeZoneId;
    set({ isLoading: true, error: null });
    try {
      const settings = await settingsService.getSettings();
      if (request !== settingsRequest || useZoneStore.getState().activeZoneId !== zoneId) return;
      const clonedSettings = deepClone(settings);
      set({
        loadedZoneId: zoneId,
        settings: clonedSettings,
        originalSettings: deepClone(clonedSettings),
        thresholds: clonedSettings.thresholdSettings ?? DEFAULT_THRESHOLD_SETTINGS,
        isLoading: false,
        hasUnsavedChanges: false
      });
    } catch (error) {
      if (request !== settingsRequest || useZoneStore.getState().activeZoneId !== zoneId) return;
      set({
        error: 'Einstellungen konnten nicht geladen werden',
        isLoading: false
      });
      console.error('[SettingsStore] Load error:', error);
    }
  },

  updateSettings: (newSettings: Settings) => {
    set(produce((state: SettingsState) => {
      state.settings = deepClone(newSettings);
      state.thresholds = newSettings.thresholdSettings ?? DEFAULT_THRESHOLD_SETTINGS;
      state.hasUnsavedChanges = JSON.stringify(newSettings) !== JSON.stringify(state.originalSettings);
    }));
  },

  saveSettings: async () => {
    const { settings } = get();
    if (!settings) return;

    set({ isLoading: true, error: null });
    try {
      await settingsService.updateSettings(settings);
      set({
        originalSettings: deepClone(settings),
        hasUnsavedChanges: false,
        isLoading: false
      });
    } catch (error) {
      set({
        error: 'Einstellungen konnten nicht gespeichert werden',
        isLoading: false
      });
      console.error('[SettingsStore] Save error:', error);
    }
  },

  discardChanges: () => {
    const { originalSettings } = get();
    set({
      settings: originalSettings ? deepClone(originalSettings) : null,
      thresholds: originalSettings?.thresholdSettings ?? DEFAULT_THRESHOLD_SETTINGS,
      hasUnsavedChanges: false
    });
  }
}));
